import { apiConfig } from '../config/api';
import { apiService } from './api';
import type { AppLocale } from '../i18n';

export interface UiTranslationDto {
  id: number;
  namespace: string;
  translationKey: string;
  locale: string;
  value: string;
  createdAt: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface UiTranslationBundleDto {
  locale: string;
  entries: Record<string, string>;
  serverTimestamp?: string;
}

export interface UiTranslationVersionDto {
  locale: string;
  version: string;
}

export interface PublishedUiTranslationFileDto {
  version: string;
  locale: string;
  entries: Record<string, string>;
}

export interface UiTranslationListResult {
  items: UiTranslationDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface UiTranslationSearchParams {
  namespace?: string;
  locale?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface UiTranslationKeyRef {
  namespace: string;
  translationKey: string;
  locale: string;
}

/** Tiny version check — client refetches static JSON only when this changes. */
export async function fetchUiTranslationVersion(locale: AppLocale): Promise<string> {
  const raw = await apiService.get<UiTranslationVersionDto>(`/api/System/ui-translations/${locale}/version`, {
    cache: true,
    cacheTTL: 30_000,
  });
  return raw.version ?? '0';
}

/**
 * Published static bundle from wwwroot/i18n/{locale}.json (regenerated on admin save).
 * Version query param busts browser cache when translations change.
 */
export async function fetchPublishedUiTranslationBundle(
  locale: AppLocale,
  version: string
): Promise<Record<string, string>> {
  const base = apiConfig.getBaseUrl().replace(/\/$/, '');
  const url = `${base}/i18n/${locale}.json?v=${encodeURIComponent(version)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    cache: 'default',
  });
  if (!response.ok) {
    throw new Error(`Failed to load published UI translations (${response.status})`);
  }
  const data = (await response.json()) as PublishedUiTranslationFileDto;
  return data.entries ?? {};
}

/** @deprecated Prefer fetchPublishedUiTranslationBundle after version check. */
export async function fetchUiTranslationBundle(locale: AppLocale): Promise<UiTranslationBundleDto> {
  const raw = await apiService.get<UiTranslationBundleDto>(`/api/System/ui-translations/${locale}`, {
    cache: true,
    cacheTTL: 120_000,
  });
  return {
    locale: raw.locale ?? locale,
    entries: raw.entries ?? {},
    serverTimestamp: raw.serverTimestamp,
  };
}

function normalizeUiTranslationRow(
  row: UiTranslationDto & {
    Id?: number;
    Namespace?: string;
    TranslationKey?: string;
    Locale?: string;
    Value?: string;
    CreatedAt?: string;
    UpdatedAt?: string;
    UpdatedBy?: string;
  }
): UiTranslationDto {
  return {
    id: row.id ?? row.Id ?? 0,
    namespace: row.namespace ?? row.Namespace ?? '',
    translationKey: row.translationKey ?? row.TranslationKey ?? '',
    locale: row.locale ?? row.Locale ?? '',
    value: row.value ?? row.Value ?? '',
    createdAt: row.createdAt ?? row.CreatedAt ?? '',
    updatedAt: row.updatedAt ?? row.UpdatedAt,
    updatedBy: row.updatedBy ?? row.UpdatedBy,
  };
}

function normalizeUiTranslationListResult(raw: UiTranslationListResult & {
  Items?: UiTranslationDto[];
  TotalCount?: number;
  Page?: number;
  PageSize?: number;
}): UiTranslationListResult {
  const items = raw.items ?? raw.Items ?? [];
  return {
    items: items.map(normalizeUiTranslationRow),
    totalCount: raw.totalCount ?? raw.TotalCount ?? 0,
    page: raw.page ?? raw.Page ?? 1,
    pageSize: raw.pageSize ?? raw.PageSize ?? 50,
  };
}

export async function resolveUiTranslationOverrides(
  keys: UiTranslationKeyRef[]
): Promise<UiTranslationDto[]> {
  if (keys.length === 0) return [];
  const raw = await apiService.post<Array<UiTranslationDto & {
    Id?: number;
    Namespace?: string;
    TranslationKey?: string;
    Locale?: string;
    Value?: string;
    CreatedAt?: string;
    UpdatedAt?: string;
    UpdatedBy?: string;
  }>>('/api/Admin/ui-translations/resolve-keys', keys, { cache: false });
  return (raw ?? []).map(normalizeUiTranslationRow);
}

export async function searchUiTranslations(params: UiTranslationSearchParams): Promise<UiTranslationListResult> {
  const qs = new URLSearchParams();
  if (params.namespace) qs.set('namespace', params.namespace);
  if (params.locale) qs.set('locale', params.locale);
  if (params.search) qs.set('search', params.search);
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const query = qs.toString();
  const raw = await apiService.get<UiTranslationListResult & { Items?: UiTranslationDto[]; TotalCount?: number }>(
    `/api/Admin/ui-translations${query ? `?${query}` : ''}`,
    { cache: false }
  );
  return normalizeUiTranslationListResult(raw);
}

export async function upsertUiTranslation(payload: {
  namespace: string;
  translationKey: string;
  locale: string;
  value: string;
}): Promise<UiTranslationDto> {
  const raw = await apiService.post<UiTranslationDto & {
    Id?: number;
    Namespace?: string;
    TranslationKey?: string;
    Locale?: string;
    Value?: string;
    CreatedAt?: string;
    UpdatedAt?: string;
    UpdatedBy?: string;
  }>('/api/Admin/ui-translations', payload);
  return normalizeUiTranslationRow(raw);
}

export async function deleteUiTranslation(id: number): Promise<void> {
  await apiService.delete(`/api/Admin/ui-translations/${id}`);
}
